import React, { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Download, Save, FileText, Plus, Trash2, Sparkles, Languages, ListFilter } from "lucide-react";

const LANGS = [
  { v: "en", l: "English" },
  { v: "hi", l: "Hindi · हिन्दी" },
  { v: "mr", l: "Marathi · मराठी" },
  { v: "ta", l: "Tamil · தமிழ்" },
  { v: "gu", l: "Gujarati · ગુજરાતી" },
];

const ALL_SECTIONS = [
  { v: "overview",      l: "Overview & Design" },
  { v: "sales",         l: "Sales" },
  { v: "purchase",      l: "Purchase" },
  { v: "banking",       l: "Banking" },
  { v: "inventory",     l: "Inventory" },
  { v: "gatepass",      l: "Gate Pass" },
  { v: "manufacturing", l: "Manufacturing" },
  { v: "compliance",    l: "Statutory Compliance" },
  { v: "hr",            l: "Human Resources" },
  { v: "reports",       l: "Reports & Consolidation" },
  { v: "migration",     l: "Migration Tools" },
  { v: "dsc",           l: "Digital Signatures" },
  { v: "case_study",    l: "Case Study (Real Numbers)" },
  { v: "security",      l: "Security & Roles" },
];

const PRESETS = [
  { v: "full",             l: "Full brochure (all sections)",     sections: null },
  { v: "manufacturing",    l: "Manufacturing-focus (Overview + Mfg + Case Study)", sections: ["overview","manufacturing","case_study"] },
  { v: "compliance",       l: "Compliance-focus (Overview + Compliance + DSC + Case Study)", sections: ["overview","compliance","dsc","case_study"] },
  { v: "finance",          l: "Finance-focus (Sales + Purchase + Banking + Reports + Case Study)", sections: ["overview","sales","purchase","banking","reports","case_study"] },
];

const EMPTY_METRIC = { label: "", before: "", after: "", delta: "" };

export default function BrochureDesigner() {
  const [cs, setCs] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preset, setPreset] = useState("full");
  const [selected, setSelected] = useState(ALL_SECTIONS.map((s) => s.v));
  const [lang, setLang] = useState("en");

  useEffect(() => { api.get("/brochure/case-study").then((r) => setCs(_hydrate(r.data))); }, []);

  const _hydrate = (data) => ({
    customer_name: data?.customer_name || "",
    industry: data?.industry || "",
    team_size: data?.team_size || "",
    narrative: data?.narrative || "",
    metrics: (data?.metrics && data.metrics.length ? data.metrics : [EMPTY_METRIC, EMPTY_METRIC, EMPTY_METRIC]),
    testimonial: {
      quote: data?.testimonial?.quote || "",
      name:  data?.testimonial?.name || "",
      role:  data?.testimonial?.role || "",
    },
  });

  const applyPreset = (val) => {
    setPreset(val);
    const p = PRESETS.find((x) => x.v === val);
    setSelected(p.sections || ALL_SECTIONS.map((s) => s.v));
  };
  const toggleSection = (slug) => {
    setPreset("custom");
    setSelected((cur) => cur.includes(slug) ? cur.filter((s) => s !== slug) : [...cur, slug]);
  };

  const setMetric = (i, patch) => setCs({ ...cs, metrics: cs.metrics.map((m, j) => j === i ? { ...m, ...patch } : m) });
  const addMetric = () => setCs({ ...cs, metrics: [...cs.metrics, { ...EMPTY_METRIC }] });
  const removeMetric = (i) => setCs({ ...cs, metrics: cs.metrics.filter((_, j) => j !== i) });

  const saveCS = async () => {
    setBusy(true);
    try {
      const payload = { ...cs, metrics: cs.metrics.filter((m) => m.label && m.after) };
      await api.post("/brochure/case-study", payload);
      toast.success("Case study saved — will appear on your next brochure download");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const downloadPDF = () => {
    const sections = selected.length === ALL_SECTIONS.length ? "" : selected.join(",");
    const qs = new URLSearchParams();
    if (sections) qs.set("sections", sections);
    if (lang !== "en") qs.set("lang", lang);
    const url = `${API}/brochure/product-guide.pdf${qs.toString() ? "?" + qs.toString() : ""}`;
    window.open(url, "_blank", "noopener");
  };

  if (!cs) return <div className="p-8 text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-5" data-testid="brochure-designer">
      <Card className="p-5 rounded-sm border-2 border-accent/30 bg-accent/5">
        <div className="flex items-start gap-3">
          <Sparkles className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-accent">Sales collateral</div>
            <div className="text-lg font-heading font-bold">Product Brochure Designer</div>
            <div className="text-sm text-muted-foreground mt-1">Configure the case-study insert, pick the sections your prospect needs, choose a language and download a tailored PDF ready to email.</div>
          </div>
        </div>
      </Card>

      {/* Case Study editor */}
      <Card className="p-5 rounded-sm space-y-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-accent flex items-center gap-1"><FileText className="w-3 h-3" /> Case study insert</div>
          <div className="text-sm text-muted-foreground">2-page section at the end of the brochure with your real before/after numbers.</div>
        </div>
        <div className="grid md:grid-cols-3 gap-3">
          <div><Label>Customer name</Label><Input value={cs.customer_name} onChange={(e) => setCs({ ...cs, customer_name: e.target.value })} className="rounded-sm mt-1" placeholder="ACME Precision Pvt Ltd" data-testid="cs-customer" /></div>
          <div><Label>Industry</Label><Input value={cs.industry} onChange={(e) => setCs({ ...cs, industry: e.target.value })} className="rounded-sm mt-1" placeholder="Tier-2 auto ancillary" data-testid="cs-industry" /></div>
          <div><Label>Team / size</Label><Input value={cs.team_size} onChange={(e) => setCs({ ...cs, team_size: e.target.value })} className="rounded-sm mt-1" placeholder="180 employees · 3 plants" data-testid="cs-size" /></div>
        </div>
        <div><Label>The story</Label>
          <Textarea rows={3} value={cs.narrative} onChange={(e) => setCs({ ...cs, narrative: e.target.value })} className="rounded-sm mt-1" placeholder="A short paragraph on how the customer migrated / what changed…" data-testid="cs-narrative" />
        </div>

        <div>
          <Label className="flex items-center justify-between">
            <span>Before / after metrics</span>
            <Button size="sm" variant="outline" onClick={addMetric} className="rounded-sm gap-1 h-7 text-xs"><Plus className="w-3 h-3" /> Add</Button>
          </Label>
          <div className="mt-2 space-y-2">
            {cs.metrics.map((m, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`cs-metric-${i}`}>
                <Input value={m.label}  onChange={(e) => setMetric(i, { label: e.target.value })}  className="col-span-4 rounded-sm h-8" placeholder="Month-close time" />
                <Input value={m.before} onChange={(e) => setMetric(i, { before: e.target.value })} className="col-span-3 rounded-sm h-8" placeholder="12 days" />
                <Input value={m.after}  onChange={(e) => setMetric(i, { after: e.target.value })}  className="col-span-3 rounded-sm h-8" placeholder="3 days" />
                <Input value={m.delta}  onChange={(e) => setMetric(i, { delta: e.target.value })}  className="col-span-1 rounded-sm h-8 font-mono text-emerald-700" placeholder="−75%" />
                <button onClick={() => removeMetric(i)} className="col-span-1 text-red-500 hover:text-red-700 text-center"><Trash2 className="w-3.5 h-3.5 mx-auto" /></button>
              </div>
            ))}
          </div>
        </div>

        <div>
          <Label>Testimonial (optional)</Label>
          <Textarea rows={2} value={cs.testimonial.quote} onChange={(e) => setCs({ ...cs, testimonial: { ...cs.testimonial, quote: e.target.value } })} className="rounded-sm mt-1" placeholder='"We stopped calling accountants at 11 pm on filing day…"' data-testid="cs-quote" />
          <div className="grid md:grid-cols-2 gap-3 mt-2">
            <Input value={cs.testimonial.name} onChange={(e) => setCs({ ...cs, testimonial: { ...cs.testimonial, name: e.target.value } })} className="rounded-sm" placeholder="Author name" data-testid="cs-quote-name" />
            <Input value={cs.testimonial.role} onChange={(e) => setCs({ ...cs, testimonial: { ...cs.testimonial, role: e.target.value } })} className="rounded-sm" placeholder="Founder & MD, ACME…" data-testid="cs-quote-role" />
          </div>
        </div>

        <Button onClick={saveCS} disabled={busy} className="rounded-sm bg-accent hover:bg-accent/90 text-white gap-1.5" data-testid="cs-save">
          <Save className="w-4 h-4" /> {busy ? "Saving…" : "Save case study"}
        </Button>
      </Card>

      {/* Preview & Download */}
      <Card className="p-5 rounded-sm space-y-4">
        <div className="text-[10px] font-mono uppercase tracking-widest text-accent flex items-center gap-1"><Download className="w-3 h-3" /> Preview &amp; download</div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label className="flex items-center gap-1"><ListFilter className="w-3 h-3" /> Preset</Label>
            <Select value={preset} onValueChange={applyPreset}>
              <SelectTrigger className="rounded-sm mt-1" data-testid="preset-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PRESETS.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}
                <SelectItem value="custom">Custom (pick sections below)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="flex items-center gap-1"><Languages className="w-3 h-3" /> Language</Label>
            <Select value={lang} onValueChange={setLang}>
              <SelectTrigger className="rounded-sm mt-1" data-testid="lang-select"><SelectValue /></SelectTrigger>
              <SelectContent>{LANGS.map((l) => <SelectItem key={l.v} value={l.v}>{l.l}</SelectItem>)}</SelectContent>
            </Select>
            {lang !== "en" && <div className="text-[10px] mt-1 text-muted-foreground">First generation may take ~30 s while translations are cached.</div>}
          </div>
        </div>

        <div>
          <Label>Sections included ({selected.length}/{ALL_SECTIONS.length})</Label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-1 mt-2">
            {ALL_SECTIONS.map((s) => (
              <button key={s.v} onClick={() => toggleSection(s.v)}
                className={`text-left text-xs px-3 py-2 rounded-sm border transition-colors ${selected.includes(s.v) ? "bg-accent/10 border-accent/40 text-accent" : "bg-white border-border text-muted-foreground hover:border-accent/30"}`}
                data-testid={`section-toggle-${s.v}`}>
                <span className="font-mono text-[9px] uppercase tracking-widest opacity-60 mr-1">{selected.includes(s.v) ? "✓" : "○"}</span>
                {s.l}
              </button>
            ))}
          </div>
        </div>

        <Button onClick={downloadPDF} disabled={selected.length === 0} className="w-full rounded-sm bg-accent hover:bg-accent/90 text-white gap-1.5 h-11" data-testid="brochure-download">
          <Download className="w-4 h-4" /> Download tailored brochure PDF
          {lang !== "en" && <span className="text-[10px] font-mono uppercase ml-1 opacity-80">· {LANGS.find((l) => l.v === lang)?.l.split(" ")[0]}</span>}
        </Button>
      </Card>
    </div>
  );
}
